/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { UserUiData } from '@veeam-vspc/models/web-controllers';
import { action, makeObservable, observable } from 'mobx';

import type { AgentsUiVisibility, InfrastructureUiVisibility, PulseUiVisibility, UiVisibility } from '@veeam-vspc/models/web-controllers';

import { BaseAddon } from '../base-addon';
import { UiSupplier } from '../../suppliers/web';
import { deepEqual, isErrorThatNeedToBeShown, TimeUnitsInMs } from '../../core';

import type { PortalPreferencesConfig } from './interfaces';
import type { AddonContext } from '../base-addon';
import type { RequestSuccessResponse } from '../../interfaces';

export class PortalPreferencesAddon extends BaseAddon {

    protected readonly url: string;
    protected readonly updateDelay: number;
    protected readonly uiSupplier: UiSupplier;

    @observable preferences: UserUiData = {};
    @observable preferencesWithoutPortalFilter: UserUiData = {};

    protected updaterId: number | null = null;

    constructor(addonContext: AddonContext, portalPreferencesConfig: PortalPreferencesConfig = {}) {
        super(addonContext);

        makeObservable(this);

        const {
            url = '/Ui/GetPreferences',
            updateDelay = 5 * TimeUnitsInMs.Minute,
        } = portalPreferencesConfig ?? {};

        this.url = url;
        this.updateDelay = updateDelay;
        this.uiSupplier = new UiSupplier(this.services.transportService);
    }

    @action
    init(): Promise<void> {
        if (!this.noAuth) {
            return Promise.all([
                this.loadPreferences(),
                this.loadPreferences(true),
            ])
                .then(() => {});
        }

        this.setPreferences({});
        this.setPreferencesWithoutPortalFilter({});

        return Promise.resolve();
    }

    @action
    destroy(): void {
        this.stopPreferenceUpdater();
        this.setPreferences({});
        this.setPreferencesWithoutPortalFilter({});
    }

    @action
    forceReloadPreferences(): Promise<void> {
        return this.init();
    }

    startPreferencesUpdater(forceTime?: number): void {
        this.updaterId = window.setInterval(() => {
            this.forceReloadPreferences();
        }, forceTime ?? this.updateDelay);
    }

    stopPreferenceUpdater(): void {
        if (this.updaterId !== null) {
            clearInterval(this.updaterId);
            this.updaterId = null;
        }
    }

    isViewVisible(
        viewName: keyof UiVisibility,
        secondName?: keyof InfrastructureUiVisibility,
        thirdName?: keyof AgentsUiVisibility | keyof PulseUiVisibility
    ): boolean {
        return this.isViewVisibleInPreferences(this.preferences, viewName, secondName, thirdName);
    }

    isViewVisibleWithoutPortalFilter(
        viewName: keyof UiVisibility,
        secondName?: keyof InfrastructureUiVisibility,
        thirdName?: keyof AgentsUiVisibility | keyof PulseUiVisibility
    ): boolean {
        return this.isViewVisibleInPreferences(this.preferencesWithoutPortalFilter, viewName, secondName, thirdName);
    }

    protected isViewVisibleInPreferences(
        preferences: UserUiData,
        viewName: keyof UiVisibility,
        secondName?: keyof InfrastructureUiVisibility,
        thirdName?: keyof AgentsUiVisibility | keyof PulseUiVisibility
    ): boolean {
        if (!secondName) {
            return preferences.visibility?.[viewName] as boolean ?? false;
        }

        if (!thirdName) {
            return preferences.visibility?.[viewName][secondName] as boolean ?? false;
        }

        return preferences.visibility?.[viewName][secondName][thirdName] as boolean ?? false;
    }

    @action
    protected loadPreferences(isPortalFiltersDisabled = false): Promise<void> {
        return this.uiSupplier.getPreferences(this.url, { rcopAutoUpdate: true, disableGlobalFilters: isPortalFiltersDisabled })
            .then((resp: RequestSuccessResponse<UserUiData>) => {
                const currentPreferences = isPortalFiltersDisabled
                    ? this.preferencesWithoutPortalFilter
                    : this.preferences;

                if (!deepEqual(currentPreferences, resp.data)) {
                    isPortalFiltersDisabled
                        ? this.setPreferencesWithoutPortalFilter(resp.data)
                        : this.setPreferences(resp.data);
                }
            })
            .catch((err) => {
                isErrorThatNeedToBeShown(err) && console.error(err);
                isPortalFiltersDisabled
                    ? this.setPreferencesWithoutPortalFilter({})
                    : this.setPreferences({});
            });
    }

    @action.bound
    protected setPreferences(preferences: UserUiData): void {
        this.preferences = preferences;
    }

    @action.bound
    protected setPreferencesWithoutPortalFilter(preferencesWithoutPortalFilter: UserUiData): void {
        this.preferencesWithoutPortalFilter = preferencesWithoutPortalFilter;
    }

}
